import React, { useState, FocusEvent } from "react";
import { Row, Col, Button, Dropdown } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { ActionHandlerFunction } from "@preveil-api";
import { dayjs, UIManagerActionTypes } from "src/common";
import { Icon } from "src/components";
import { DateSelector } from ".";

type AllProps = {
  handleAction: ActionHandlerFunction;
}

function DateSelectorDropdownComponent(props: AllProps) {
  const { handleAction } = props;
  const now = dayjs().toDate();
  const [start_date, setStartDate] = useState<Date | null>(dayjs().subtract(2, "week").toDate());
  const [end_date, setEndDate] = useState<Date | null>(now);
  const [active_item, setActiveItem] = useState<string>("");
  const [show_date_picker, setShowDatePicker] = useState<boolean>(false);
  const [show_dropdown, setShowDropdown] = useState<boolean>(false);

  const handleChange = (dates: [Date, Date]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    setShowDatePicker(true);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement, Element>) => {
    const input = event.target.value;
    if (input) {
      setActiveItem("custom range");
      const dates = input.split(" - ");
      const start = dayjs(new Date(dates[0]).toISOString());
      const end = dayjs(new Date(dates[1]).toISOString());
      const s_date =
        start.isValid() && start < end && start < dayjs() ? start.toDate() : start_date || now;
      const e_date = end.isValid() && end > start && end < dayjs() ? end.toDate() : end_date || now;
      handleChange([s_date, e_date]);
    }
  };

  const handleClick = (date: dayjs.Dayjs, active_item: string) => {
    handleChange([date.toDate(), now]);
    setShowDatePicker(false);
    setShowDropdown(!show_dropdown);
    setActiveItem(active_item);
    handleAction({
      actionType: UIManagerActionTypes.DateSelector,
      params: { start: date.toDate(), end: now, selected_range: active_item },
    });
  };

  const handleSubmit = () => {
    setShowDatePicker(false);
    setShowDropdown(false);
    handleAction({
      actionType: UIManagerActionTypes.DateSelector,
      params: { start: start_date, end: end_date, selected_range: active_item },
    });
  };

  const DateSelectorDropdown = () => (
    <Dropdown className="date-picker-dropdown" show={show_dropdown}>
      <div
        className="date-picker-input main-picker"
        onClick={() => {
          setShowDropdown(true);
        }} >
        <span>
          <Icon className="pv-icon-calendar text-muted" />
        </span>
        <DatePicker
          className="border-0 input"
          selected={start_date}
          startDate={start_date}
          endDate={end_date}
          onChange={handleChange}
          onBlur={handleBlur}
          popperClassName="d-none"
          selectsRange
          dateFormat="MMM d, yyyy"
          placeholderText="Select start date"
          maxDate={now}
          shouldCloseOnSelect={false}
        />
      </div>
      <Dropdown.Menu className="p-0 date-picker-menu">
        <Row className="ms-0 me-0">
          <Col
            xs={show_date_picker ? "3" : undefined}
            className={!!show_date_picker ? "ranges right-border" : "ranges"}>
            <Dropdown.Item
              className={active_item === "last week" ? "active" : ""}
              onClick={() => handleClick(dayjs().subtract(1, "week"), "last week")}>
              Past Week
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() => handleClick(dayjs().subtract(2, "week"), "last two weeks")}
              className={active_item === "last two weeks" ? "active" : ""}  >
              Past Two Weeks
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() => handleClick(dayjs().subtract(1, "month"), "last month")}
              className={active_item === "last month" ? "active" : ""} >
              Past Month
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() => handleClick(dayjs().subtract(6, "month"), "last 6 months")}
              className={active_item === "last 6 months" ? "active" : ""}>
              Past 6 Months
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() => handleClick(dayjs().subtract(1, "year"), "last year")}
              className={active_item === "last year" ? "active" : ""}>
              Past Year
            </Dropdown.Item>
            <Dropdown.Item
              // When the "Forever" tab is selected, set the start date to 20180101 (release data of the product)
              onClick={() => handleClick(dayjs("2018-01-01"), "forever")}
              className={active_item === "forever" ? "active" : ""}>
              Forever
            </Dropdown.Item>
            <Dropdown.Item
              className={active_item === "custom range" ? "active" : ""}
              onClick={() => {
                setShowDatePicker(true);
                setActiveItem("custom range");
              }}>
              Custom Range
            </Dropdown.Item>
            <Dropdown.Item className="buttons">
              {" "}
              <Button
                variant="primary"
                className="me-2"
                disabled={!start_date || !end_date}
                onClick={() => handleSubmit()}
              >
                Apply
              </Button>
              <Button
                variant="no-outline-primary"
                onClick={() => setShowDropdown(false)}
              >
                Cancel
              </Button>
            </Dropdown.Item>
          </Col>
          {show_date_picker ? (
            <Col className="selector">
              <DateSelector
                startDate={start_date}
                endDate={end_date}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                handleChange={handleChange}
              ></DateSelector>
            </Col>
          ) : null}
        </Row>
      </Dropdown.Menu>
    </Dropdown>
  );

  return <DateSelectorDropdown />;
}

export default React.memo(DateSelectorDropdownComponent);